import { observer } from 'mobx-react-lite';
import type { CalendarSurfaceItemContentProps } from './CalendarSurfaceItem.types';
import calculateFieldSizings from '../../utils/calculateFieldSizings';
import calculateIconLocationSizings, { IconLocationType } from '../../utils/calculateIconLocation';
import getInnerHeight from '../../utils/getInnerHeight';
import CalendarSurfaceItemClipping from '../CalendarSurfaceItemClipping';
import {
    renderCharmWrapper,
    renderIconWrapper,
    renderJoinButton,
    hasIcon,
} from '../CalendarSurfaceItemComponentRenderer';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import * as React from 'react';
import {
    getCalendarEventItemSubjectText,
    getCalendarEventItemLocationText,
} from 'owa-calendar-event-attributes';
import { isEventJoinableNow } from 'owa-online-meeting/lib/bootIndex';
import { useCustomAnimationFrame } from 'owa-react-hooks/lib/useCustomAnimationFrame';
import getInnerWidth from '../../utils/getInnerWidth';
import { createSubjectGridStyleWithJoinButton } from '../../utils/createSubjectGridStyle';
import { differenceInMinutes } from 'owa-datetime';

const styles = require('../CalendarSurfaceItem.scss');
let classNames = require('classnames');

interface CalendarSurfaceItem60OrMoreState {
    locationLines: number;
    orgLines: number;
    subjectHeight: number;
    lineHeights: number[];
    iconLocation: IconLocationType;
    joinButtonWidth: number;
}

/**
 * The Component for a calendar surface item over 60 minutes in length
 */
export default observer(function CalendarSurfaceItem60OrMore(
    props: CalendarSurfaceItemContentProps
) {
    const wrapperRef = React.useRef<HTMLDivElement>();
    const subjectRef = React.useRef<HTMLHeadingElement>();
    const locationRef = React.useRef<HTMLDivElement>();
    const orgRef = React.useRef<HTMLDivElement>();
    const joinWrapperRef = React.useRef<HTMLDivElement>();

    const [
        surfaceItemState,
        setSurfaceItemState,
    ] = React.useState<CalendarSurfaceItem60OrMoreState>({
        locationLines: 0,
        orgLines: 0,
        subjectHeight: 0,
        lineHeights: [0, 0, 0],
        iconLocation: IconLocationType.Subject,
        joinButtonWidth: 0,
    });

    const [isJoinableNow, setIsJoinableNow] = React.useState<boolean>(false);
    const currIsJoinableNow = props.joinButtonVisible && isEventJoinableNow(props.item);

    React.useEffect(() => {
        if (isJoinableNow != currIsJoinableNow) {
            setIsJoinableNow(currIsJoinableNow);
        }
    });

    const CreateHeaderEntry = (subject: string) => {
        const realSubject = subject ? subject : location;
        const gridGapStyle = currIsJoinableNow ? styles.headerContainerGridGap : null;
        const headerContainerStyle = classNames(styles.headerContainer, gridGapStyle);
        const subjectHasSeparators = /\s/g.test(realSubject);
        const currJoinButtonWidth = joinWrapperRef.current
            ? getInnerWidth(joinWrapperRef.current)
            : 0;
        const currItemWidth = wrapperRef.current ? getInnerWidth(wrapperRef.current) : 0;

        const headerWrapper = (
            <div
                className={headerContainerStyle}
                style={createSubjectGridStyleWithJoinButton(
                    currItemWidth,
                    currJoinButtonWidth,
                    currIsJoinableNow,
                    differenceInMinutes(item.End, item.Start),
                    getInnerHeight(wrapperRef.current), // eventHeight
                    surfaceItemState.subjectHeight,
                    !!location, // itemHasLocation
                    item.IsMeeting,
                    subjectHasSeparators,
                    hasIcon(item, null)
                )}>
                <h2 ref={subjectRef} className={styles.itemSubject}>
                    {renderCharmWrapper(item, colorScheme, charmId)}
                    <span className={styles.itemSubject}>{realSubject}</span>
                </h2>
                {currIsJoinableNow ? (
                    <div ref={joinWrapperRef} className={styles.joinButtonWrapper}>
                        {renderJoinButton(
                            item,
                            colorScheme,
                            currItemWidth,
                            currJoinButtonWidth,
                            styles.joinButton
                        )}
                    </div>
                ) : null}
            </div>
        );

        return headerWrapper;
    };

    const resetFieldSizing = () => {
        const lineHeights = [
            SurfaceConstants.subjectLineHeightWithButton,
            SurfaceConstants.detailsLineHeight,
            SurfaceConstants.detailsLineHeight,
        ];
        const totalHeight = getInnerHeight(wrapperRef.current);
        let subjectHeight = getInnerHeight(subjectRef.current);
        const locationHeight = getInnerHeight(locationRef.current);
        const orgHeight = getInnerHeight(orgRef.current);

        // Ensure subjectHeight does not exceed the item's height or to be 0
        if (subjectHeight === 0 || subjectHeight >= totalHeight) {
            subjectHeight = totalHeight - locationHeight - orgHeight;
        }

        const lines = calculateFieldSizings(lineHeights, totalHeight, [
            subjectHeight,
            locationHeight,
            orgHeight,
        ]);

        // making room for the iconwrapper:
        // if the subject is going down to the bottom, it'll overlap- remove bottom line of subject
        if (subjectHeight >= totalHeight - lineHeights[0] && lines[1] == 0 && lines[0] > 1) {
            lines[0] = lines[0] - 1;
        }
        const iconLocation = calculateIconLocationSizings(
            lineHeights,
            lines,
            SurfaceConstants.detailsLineHeight,
            totalHeight
        );

        const joinButtonWidth = joinWrapperRef.current ? getInnerWidth(joinWrapperRef.current) : 0;

        setSurfaceItemState({
            locationLines: lines[1],
            orgLines: lines[2],
            subjectHeight,
            lineHeights,
            iconLocation,
            joinButtonWidth,
        });
    };

    const [requestFieldSizingFrame] = useCustomAnimationFrame();
    React.useLayoutEffect(() => {
        requestFieldSizingFrame(resetFieldSizing);
    }, []);

    const { item, colorScheme, charmId, allowEmptySubject } = props;

    const subject = getCalendarEventItemSubjectText(item, allowEmptySubject);
    const location = getCalendarEventItemLocationText(item);
    const organizer = item.Organizer && item.Organizer.Mailbox.Name;

    return (
        <div ref={wrapperRef} className={styles.itemContent}>
            {CreateHeaderEntry(subject)}
            {location && (
                <div className={styles.itemElementContent} ref={locationRef}>
                    <CalendarSurfaceItemClipping
                        lineHeight={surfaceItemState.lineHeights[1]}
                        lines={surfaceItemState.locationLines}
                        whiteSpace={'nowrap'}>
                        {subject ? location : null}
                    </CalendarSurfaceItemClipping>
                    {surfaceItemState.iconLocation == IconLocationType.Location &&
                        renderIconWrapper(item, false)}
                </div>
            )}

            {item.IsMeeting && (
                <div className={styles.itemElementContent} ref={orgRef}>
                    <CalendarSurfaceItemClipping
                        lineHeight={surfaceItemState.lineHeights[2]}
                        lines={surfaceItemState.orgLines}
                        whiteSpace={'nowrap'}>
                        {organizer}
                    </CalendarSurfaceItemClipping>
                    {surfaceItemState.iconLocation == IconLocationType.Organizer &&
                        renderIconWrapper(item, false)}
                </div>
            )}
            {surfaceItemState.iconLocation == IconLocationType.BelowAll &&
                renderIconWrapper(item, false)}
        </div>
    );
});
